import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTranslation } from '@/hooks/useTranslation';
import { Card } from '@/components/ui/card/Card';
import { COLORS } from '../../utils/palette';
import type { ReferenceMetricsAggregation } from '../../utils/aggregateToolMetricsByReference';

export type MeanMetric = 'duration' | 'pieces' | 'volume' | 'malround';

type MeanMetricChartProps = {
  /** Selects the aggregated field, its translations, the value formatter and the bar colour. */
  metric: MeanMetric;
  data: ReferenceMetricsAggregation[];
};

interface MetricDisplay {
  /** Segment of the `analytics.comparison.performance.<i18n>.*` translation keys. */
  i18n: string;
  dataKey: keyof ReferenceMetricsAggregation;
  format: (value: number) => string;
  color: string;
}

/** Seconds → s / min / h */
function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = seconds / 60;
  if (minutes < 60) {
    return `${minutes.toFixed(1)} min`;
  }
  const hours = minutes / 60;
  return `${hours.toFixed(2)}h`;
}

function formatPieces(value: number): string {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toFixed(0);
}

/** cm³, or L from 1000 cm³ */
function formatVolume(value: number): string {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(2)} L`;
  }
  return `${value.toFixed(1)} cm³`;
}

function formatMalround(value: number): string {
  return `${value.toFixed(2)} µm`;
}

const METRICS: Record<MeanMetric, MetricDisplay> = {
  duration: {
    i18n: 'meanDuration',
    dataKey: 'meanTotalDuration',
    format: formatDuration,
    color: COLORS[0],
  },
  pieces: {
    i18n: 'meanPieces',
    dataKey: 'meanPieceNumber',
    format: formatPieces,
    color: COLORS[1],
  },
  volume: {
    i18n: 'meanVolume',
    dataKey: 'meanVolumeRemoved',
    format: formatVolume,
    color: COLORS[2],
  },
  malround: {
    i18n: 'meanMalround',
    dataKey: 'meanMalround',
    format: formatMalround,
    color: COLORS[3],
  },
};

/**
 * Bar chart of one per-reference mean. One component for the four metrics of the comparison
 * view: they only differ by the aggregated field, their labels, value formatting and colour.
 */
export function MeanMetricChart({ metric, data }: MeanMetricChartProps) {
  const { t } = useTranslation();
  const { i18n, dataKey, format, color } = METRICS[metric];

  return (
    <Card padding="sm">
      <h3 className="mb-4 text-sm font-medium text-foreground">
        {t(`analytics.comparison.performance.${i18n}.title`)}
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis dataKey="reference" tick={{ fontSize: 12 }} stroke="var(--muted-foreground)" />
          <YAxis
            label={{
              value: t(`analytics.comparison.performance.${i18n}.yAxisLabel`),
              angle: -90,
              position: 'insideLeft',
              style: { fontSize: 12, fill: 'var(--muted-foreground)' },
            }}
            tick={{ fontSize: 12 }}
            stroke="var(--muted-foreground)"
          />
          <Tooltip
            formatter={(value) => [
              format(value as number),
              t(`analytics.comparison.performance.${i18n}.tooltip`),
            ]}
            contentStyle={{
              backgroundColor: 'var(--popover)',
              color: 'var(--popover-foreground)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              fontSize: '12px',
            }}
          />
          <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}
